import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { deleteEncoder, getEncoderById, getPacienteById, updateEncoder } from '../api/encoderApi';

const EMPTY_FIELDS = {
  codigoPrueba: '',
  velocidad: '',
  potencia: '',
  distancia: '',
  tiempo5STS: '',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const toFieldValue = (value) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  return String(value);
};

const getFirstDefinedValue = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return '';
};

export const useEncoderViewer = ({ encoderId, pacienteId }) => {
  const navigate = useNavigate();
  const [patientInfo, setPatientInfo] = useState({
    codigo: '',
    createdAt: '',
    nombre: '',
    apellidos: '',
    codigoInforme: '',
  });
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [isEditing, setIsEditing] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [originalValues, setOriginalValues] = useState(EMPTY_FIELDS);

  useEffect(() => {
    if (!encoderId || !pacienteId) return;
    Promise.all([getPacienteById(pacienteId), getEncoderById(encoderId)])
      .then(([pacienteData, data]) => {
        setPatientInfo({
          codigo: String(pacienteData.id || ''),
          createdAt: formatDate(data.createdAt),
          nombre: pacienteData.firstname || '',
          apellidos: pacienteData.lastname || '',
          codigoInforme: data.id || '',
        });
        setFields({
          codigoPrueba: toFieldValue(data.codigoPrueba),
          velocidad: toFieldValue(getFirstDefinedValue(data, ['velocidad'])),
          potencia: toFieldValue(getFirstDefinedValue(data, ['potencia'])),
          distancia: toFieldValue(getFirstDefinedValue(data, ['distancia', 'distanciaRecorrida', 'distance'])),
          tiempo5STS: toFieldValue(getFirstDefinedValue(data, ['tiempo5STS', 'tiempo5sts', 'tiempo_5sts'])),
        });
      })
      .catch(console.error);
  }, [encoderId, pacienteId]);

  const setField = (name, value) => setFields((prev) => ({ ...prev, [name]: value }));

  const handleEdit = () => {
    setOriginalValues(fields);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFields(originalValues);
    setIsEditing(false);
  };

  const handleSave = () => {
    updateEncoder({
      encoderId,
      payload: {
        codigoPrueba: fields.codigoPrueba,
        velocidad: parseFloat(fields.velocidad),
        potencia: parseFloat(fields.potencia),
        distancia: parseFloat(fields.distancia),
        tiempo5STS: parseFloat(fields.tiempo5STS),
      },
    })
      .then(() => {
        setOpenGood(true);
        setIsEditing(false);
      })
      .catch(console.error);
  };

  const handleDelete = () => {
    if (!window.confirm('¿Está seguro de que desea eliminar este registro?')) return;
    deleteEncoder(encoderId)
      .then(() => { navigate('/home'); })
      .catch(console.error);
  };

  const handleExportPDF = () => {
    setIsEditing(false);
    const input = document.getElementById('pdf-content');
    html2canvas(input, {
      scale: 2,
      backgroundColor: null,
      useCORS: true,
      ignoreElements: (el) => el.tagName === 'BUTTON',
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`encoder_${patientInfo.nombre}${patientInfo.apellidos}_${patientInfo.createdAt}.pdf`);
      })
      .catch(console.error);
  };

  const metrics = useMemo(() => {
    const velocidad = parseFloat(fields.velocidad);
    const potencia = parseFloat(fields.potencia);
    const tiempo5STS = parseFloat(fields.tiempo5STS);
    return [
      { label: 'Velocidad', value: Number.isNaN(velocidad) ? 'N/D' : velocidad.toFixed(2), unit: 'm/s' },
      { label: 'Potencia', value: Number.isNaN(potencia) ? 'N/D' : potencia.toFixed(2), unit: 'W' },
      { label: 'Tiempo 5STS', value: Number.isNaN(tiempo5STS) ? 'N/D' : tiempo5STS.toFixed(2), unit: 's' },
    ];
  }, [fields.velocidad, fields.potencia, fields.tiempo5STS]);

  return {
    patientInfo,
    fields,
    isEditing,
    openGood,
    metrics,
    setOpenGood,
    setField,
    handleEdit,
    handleCancel,
    handleSave,
    handleDelete,
    handleExportPDF,
  };
};
